import { useState, useCallback, useRef } from "react";
import { collection, query, orderBy, limit, startAfter, getDocs, where, Timestamp } from "firebase/firestore";
import { db } from "../firebase";

const PAGE_SIZE = 30;

export default function useAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const lastDoc = useRef(null);
  const currentFilters = useRef({});

  const loadLogs = useCallback(async (filters = {}, append = false) => {
    setLoading(true);
    try {
      const constraints = [collection(db, "audit_log")];
      const qConstraints = [];

      if (filters.userId) qConstraints.push(where("userId", "==", filters.userId));
      if (filters.accion) qConstraints.push(where("accion", "==", filters.accion));
      if (filters.entidad) qConstraints.push(where("entidad", "==", filters.entidad));
      if (filters.dateFrom) {
        const d = new Date(filters.dateFrom);
        d.setHours(0, 0, 0, 0);
        qConstraints.push(where("timestamp", ">=", Timestamp.fromDate(d)));
      }
      if (filters.dateTo) {
        const d = new Date(filters.dateTo);
        d.setHours(23, 59, 59, 999);
        qConstraints.push(where("timestamp", "<=", Timestamp.fromDate(d)));
      }

      qConstraints.push(orderBy("timestamp", "desc"));
      qConstraints.push(limit(PAGE_SIZE));

      if (append && lastDoc.current) {
        qConstraints.push(startAfter(lastDoc.current));
      }

      const q = query(constraints[0], ...qConstraints);
      const snap = await getDocs(q);
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      lastDoc.current = snap.docs[snap.docs.length - 1] || null;
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLogs(prev => append ? [...prev, ...docs] : docs);
      currentFilters.current = filters;
    } catch (err) {
      console.error("Error loading audit log:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (hasMore && !loading) loadLogs(currentFilters.current, true);
  }, [hasMore, loading, loadLogs]);

  const exportCSV = useCallback(() => {
    if (logs.length === 0) return;
    const header = "Fecha,Usuario,Email,Acción,Entidad,ID Entidad,Detalles,Hash Antes,Hash Después";
    const rows = logs.map(l => {
      const fecha = l.timestamp?.toDate ? l.timestamp.toDate().toISOString() : "";
      const esc = (s) => `"${(s || "").replace(/"/g, '""')}"`;
      return [fecha, esc(l.userId), esc(l.userEmail), esc(l.accion), esc(l.entidad), esc(l.entidadId), esc(l.detalles), esc(l.hashAntes), esc(l.hashDespues)].join(",");
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [logs]);

  return { logs, loading, hasMore, loadLogs, loadMore, exportCSV };
}
